import React, { useEffect, useState } from "react";
import { Input } from "../ui/input";
import { cn } from "../../lib/utils";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

/**
 * Custom hex color picker. Three controls in one row:
 *   1. live preview chip (checkered placeholder when value isn't a valid hex)
 *   2. native <input type="color"> for visual picking
 *   3. text input that accepts "#rrggbb" or "rrggbb" — commits on Enter or blur
 * Works alongside <ColorSwatchPicker /> — calling onChange clears the swatch
 * selection because the parent stores a single `color` string.
 */
export function HexColorPicker({ value, onChange }) {
  const isHex = HEX_RE.test(value || "");
  // Use the input's value when it's a valid hex, otherwise show a default for the native picker.
  const colorInputValue = isHex ? value : "#3b82f6";
  const [text, setText] = useState(isHex ? value : "");

  useEffect(() => {
    setText(isHex ? value : "");
  }, [value, isHex]);

  const commitText = (raw) => {
    const trimmed = (raw || "").trim();
    if (!trimmed) return;
    const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
    if (HEX_RE.test(withHash)) {
      onChange(withHash.toLowerCase());
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="hex-color-picker">
      <span
        className={cn(
          "h-8 w-8 rounded-full ring-offset-2 ring-offset-white shrink-0",
          isHex ? "ring-2 ring-slate-900 scale-110" : "ring-1 ring-slate-200"
        )}
        style={{
          backgroundColor: isHex ? value : "transparent",
          backgroundImage: isHex
            ? "none"
            : "linear-gradient(135deg,#fff 25%,#e5e7eb 25%,#e5e7eb 50%,#fff 50%,#fff 75%,#e5e7eb 75%) 0 0 / 12px 12px",
        }}
        aria-hidden="true"
      />
      <input
        type="color"
        value={colorInputValue}
        onChange={(e) => onChange(e.target.value.toLowerCase())}
        className="h-8 w-10 rounded-md border border-slate-200 cursor-pointer p-0 bg-transparent"
        aria-label="Pick a custom color"
        data-testid="hex-color-native"
      />
      <Input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commitText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitText(e.currentTarget.value);
          }
        }}
        placeholder="#3b82f6"
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        className="h-8 w-28 font-mono text-xs"
        data-testid="hex-color-input"
      />
    </div>
  );
}
